import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Magnifier — camera + zoom + freeze + torch. CSS transform: scale() is
// the most cross-browser-reliable way to zoom (some browsers expose a
// `zoom` constraint; many don't). Torch is `torch: true` on the
// MediaStreamTrack capabilities; many devices/browsers don't support it.

export default function Magnifier() {
  const [state, setState] = useState("idle");
  const [zoom, setZoom] = useState(2);
  const [torch, setTorch] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [contrast, setContrast] = useState(100);
  const videoRef = useRef(null);
  const canvasRef = useRef(null); // for freeze frame
  const streamRef = useRef(null);
  const trackRef = useRef(null);

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      // V57.6: srcObject attachment moved to post-render useEffect.
      // The <video> element is conditionally rendered and isn't in the
      // DOM at this point — assigning to videoRef.current.srcObject
      // here would silently no-op on a null ref.
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      setTorchSupported(!!caps.torch);
      setState("ok");
    } catch (err) {
      // V57.5: differentiate error types so the UI is honest
      if (err && err.name === "NotAllowedError") setState("denied");
      else if (err && err.name === "NotFoundError") setState("nodevices");
      else setState("denied");
    }
  };

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // V57.6: attach stream after the <video> element renders.
  useEffect(() => {
    if (state === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  const toggleTorch = async () => {
    if (!trackRef.current) return;
    try {
      const next = !torch;
      await trackRef.current.applyConstraints({ advanced: [{ torch: next }] });
      setTorch(next);
    } catch {
      setTorchSupported(false);
    }
  };

  const freeze = () => {
    if (!videoRef.current || !canvasRef.current) return;
    if (frozen) { setFrozen(false); return; }
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setFrozen(true);
  };

  return (
    <ToolsShell title="Magnifier">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            High-zoom camera for reading tiny text, examining details, threading needles. Includes freeze-frame and flashlight.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable camera</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Camera" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ position: "relative", width: "100%", maxWidth: 540, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden", margin: "0 auto" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: frozen ? "none" : "block",
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
                filter: `contrast(${contrast}%)`,
              }}
            />
            <canvas
              ref={canvasRef}
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: frozen ? "block" : "none",
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
                filter: `contrast(${contrast}%)`,
              }}
            />
            {frozen && (
              <div style={{ position: "absolute", top: 10, left: 10, background: "rgba(0,0,0,0.7)", color: "#fff", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                FROZEN
              </div>
            )}
          </div>

          {/* Controls */}
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 13, color: "#9A9DBE", display: "flex", justifyContent: "space-between" }}>
              <span>Zoom</span>
              <span style={{ color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{zoom.toFixed(1)}×</span>
            </label>
            <input type="range" min="1" max="8" step="0.1" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 6 }} />
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 13, color: "#9A9DBE", display: "flex", justifyContent: "space-between" }}>
              <span>Contrast</span>
              <span style={{ color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{contrast}%</span>
            </label>
            <input type="range" min="50" max="200" step="5" value={contrast} onChange={(e) => setContrast(parseInt(e.target.value, 10))}
              style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 6 }} />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={freeze} style={{ background: frozen ? TOOLS_ACCENT : "#1C1E2E", color: frozen ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {frozen ? "Unfreeze" : "Freeze frame"}
            </button>
            {torchSupported && (
              <button onClick={toggleTorch} style={{ background: torch ? "#F8B84E" : "#1C1E2E", color: torch ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {torch ? "Torch on" : "Torch"}
              </button>
            )}
            <button onClick={() => { setZoom(2); setContrast(100); }} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              Reset
            </button>
          </div>

          {!torchSupported && (
            <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 10, textAlign: "center" }}>
              Torch control isn't available on this device/browser (common on iOS Safari).
            </p>
          )}
        </>
      )}
    </ToolsShell>
  );
}
